import { Table } from "../../../components/table-components/Table";
import { colors } from "../../../components/colors";
import { TableCellHeaderText } from "../../../components/table-components/TableCellHeaderText";
import { TableRowHeader } from "../../../components/table-components/TableRowHeader";
import { TableBody } from "../../../components/table-components/TableBody";
import { MenuCampaign } from "../models/Menus";
import { formatDateWithYear, getAutoCampaignStatus } from "../../../util";
import { formatMoneyAmount } from "../../../util";
import { TableEllipsisDropdown } from "../../../components/TableEllipsisDropdown";
import { TableRow } from "../../../components/table-components/TableRow";
import { TableCellText } from "../../../components/table-components/TableCellText";
import { StyledText } from "../../../assets/typography/typography";
import { DropdownItemLink } from "../../common/DropdownItemLink";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { routes } from "../../../appRoutingHelpers";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import { DropdownItemButton } from "../../common/DropdownItemButton";
import { useRemoveCampaignFromMenu } from "../service/menus-api";
import { useParams } from "react-router";
import { toastr } from "react-redux-toastr";
import { getErrorMessage } from "../../../error-handler";
import { DiscountTypeBadge } from "../../auto-campaigns/campaigns/components/DiscountTypeBadge";
import { EnabledBadge } from "../../auto-campaigns/campaigns/components/CampaignStatusBadge";

type Props = {
  campaigns: MenuCampaign[];
};

export const MenuCampaignsTable = ({ campaigns }: Props) => {
  const { id } = useParams<{
    id: string;
  }>();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const { mutateAsync: removeCampaignFromMenu } = useRemoveCampaignFromMenu(id);

  return (
    <Table>
      <thead>
        <TableRowHeader>
          <TableCellHeaderText>Display name</TableCellHeaderText>
          <TableCellHeaderText>Admin name</TableCellHeaderText>
          <TableCellHeaderText>Discount type</TableCellHeaderText>
          <TableCellHeaderText>Discount value</TableCellHeaderText>
          <TableCellHeaderText>Active from</TableCellHeaderText>
          <TableCellHeaderText>Active to</TableCellHeaderText>
          <TableCellHeaderText>Enabled</TableCellHeaderText>
          <th />
        </TableRowHeader>
      </thead>
      <TableBody>
        {campaigns.map((campaign, i) => {
          return (
            <TableRow key={i}>
              <TableCellText className="pl-5">
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {campaign.title}
                </StyledText>
              </TableCellText>
              <TableCellText className="pl-5">
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {campaign.name}
                </StyledText>
              </TableCellText>
              <TableCellText>
                <DiscountTypeBadge discountType={campaign.discountType} />
              </TableCellText>
              <TableCellText>
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {campaign.discountType === "fixed_price"
                    ? formatMoneyAmount(
                        campaign.discountValue,
                        campaign.currency
                      )
                    : `${campaign.discountValue}%`}
                </StyledText>
              </TableCellText>
              <TableCellText>
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {formatDateWithYear(campaign.activeFrom)}
                </StyledText>
              </TableCellText>
              <TableCellText>
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {formatDateWithYear(campaign.activeTo)}
                </StyledText>
              </TableCellText>
              <TableCellText>
                <EnabledBadge status={getAutoCampaignStatus(campaign)} />
              </TableCellText>
              <TableEllipsisDropdown
                menu={{
                  items: [
                    {
                      key: "1",
                      label: (
                        <DropdownItemLink
                          icon={
                            <FontAwesomeIcon
                              icon={solid("up-right-from-square")}
                            />
                          }
                          to={routes.campaignsEdit(
                            organizationKeyName,
                            campaign.id
                          )}
                        >
                          Go to campaign
                        </DropdownItemLink>
                      ),
                    },
                    {
                      key: "2",
                      label: (
                        <DropdownItemButton
                          icon={<FontAwesomeIcon icon={solid("trash")} />}
                          onClick={async () => {
                            if (!id) {
                              return;
                            }
                            try {
                              await removeCampaignFromMenu({
                                organizationKeyName,
                                data: {
                                  id: campaign.id,
                                  menuId: id,
                                },
                              });
                              toastr.success("Success", "Campaign removed");
                            } catch (error) {
                              const errorMessage = getErrorMessage(error);
                              toastr.error("Error", errorMessage);
                            }
                          }}
                        >
                          Remove campaign from menu
                        </DropdownItemButton>
                      ),
                    },
                  ],
                }}
              />
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
